import type { AgentsAvailability, PublicSiteConfig } from '@chat/shared';

/**
 * Contrat postMessage entre le loader (page hote, first-party) et l'iframe du
 * chat (notre domaine, donc contexte tiers pour la page hote).
 *
 * Ce contrat n'est PAS dans packages/shared : il ne concerne que ces deux
 * bundles, qui sont toujours compiles et deployes ensemble. Le serveur n'a
 * jamais a le connaitre.
 *
 * Trois protections, toutes indispensables -- un postMessage non verifie est une
 * porte ouverte sur la page hote :
 *   1. l'emetteur cible TOUJOURS une origine explicite, jamais "*" ;
 *   2. le recepteur compare event.origin a l'origine attendue ET event.source a
 *      la fenetre attendue ;
 *   3. chaque message porte le nonce de canal genere par le loader : une autre
 *      iframe de la page, meme servie par notre domaine, ne peut pas se faire
 *      passer pour le panneau.
 */

export const PROTOCOL_VERSION = 1;

/** Marqueur de provenance. Ecarte d'emblee le bruit postMessage des tiers. */
export const HOST_SOURCE = 'chat-widget/host';
export const FRAME_SOURCE = 'chat-widget/frame';

/** Choix de consentement RGPD, memorise en first-party par le loader. */
export interface LocalConsent {
  tracking: boolean;
  geo: boolean;
  /** Date ISO de la decision : permet de re-demander apres une longue periode. */
  decidedAt: string;
}

/** Page hote courante. Envoyee par le loader, qui seul la connait. */
export interface PageInfo {
  url: string;
  title: string | null;
  referrer: string | null;
}

/**
 * Etat de session memorise en first-party.
 *
 * Sert a decider si l'iframe doit etre montee des le chargement : un visiteur
 * qui a une conversation en cours doit continuer a recevoir les reponses de
 * l'agent en changeant de page, sans avoir a reouvrir le panneau.
 */
export interface LocalSession {
  hasActiveConversation: boolean;
  unread: number;
  /** Le panneau etait-il ouvert sur la page precedente ? */
  open: boolean;
  updatedAt: string;
}

export interface InitPayload {
  widgetKey: string;
  /** Origine de notre serveur : l'iframe s'en sert comme cible d'envoi. */
  serverOrigin: string;
  /** Origine de la page hote : l'iframe la transmet au serveur pour controle. */
  hostOrigin: string;
  /** `null` a la premiere visite : le serveur en emettra un. */
  visitorToken: string | null;
  /** `null` si le visiteur n'a jamais repondu au bandeau de consentement. */
  consent: LocalConsent | null;
  page: PageInfo;
  /**
   * Configuration deja recuperee par le loader (GET /api/widget/config), passee
   * ici pour eviter un second appel reseau depuis l'iframe.
   */
  site: PublicSiteConfig;
  availability: AgentsAvailability;
  /** Le panneau est-il visible a l'instant de l'init ? */
  open: boolean;
  prefersReducedMotion: boolean;
  browserTimezone: string | null;
  /**
   * Jeton d'identite signe par la boutique, quand la page hote en depose un.
   *
   * `null` dans le cas ordinaire. OPAQUE DES DEUX COTES DE CETTE FRONTIERE :
   * ni le loader ni l'iframe ne savent le lire, seul le serveur detient de quoi
   * en verifier la signature. C'est exactement ce qui donne sa valeur au
   * mecanisme -- si du code de page publique pouvait le fabriquer ou le
   * modifier, il ne prouverait plus rien.
   *
   * A NE PAS CONFONDRE avec `visitor-hint`, juste en dessous : celui-la est une
   * suggestion de pre-remplissage, que le visiteur corrige et que personne ne
   * verifie.
   */
  identityToken?: string | null;
}

/** Details pre-remplis par la page hote (plugin WordPress, par exemple). */
export interface VisitorHint {
  name?: string;
  email?: string;
  phone?: string;
}

interface Envelope<TSource extends string, TType extends string, TPayload> {
  source: TSource;
  v: number;
  channel: string;
  type: TType;
  payload: TPayload;
}

type HostMessage<TType extends string, TPayload = undefined> = Envelope<
  typeof HOST_SOURCE,
  TType,
  TPayload
>;

type FrameMessage<TType extends string, TPayload = undefined> = Envelope<
  typeof FRAME_SOURCE,
  TType,
  TPayload
>;

export type HostToFrameMessage =
  | HostMessage<'init', InitPayload>
  | HostMessage<'page', PageInfo>
  | HostMessage<'open', { focus: boolean }>
  | HostMessage<'close', undefined>
  /** Visibilite de l'onglet hote : evite d'accuser lecture d'un message non vu. */
  | HostMessage<'visibility', { visible: boolean }>
  /**
   * Le visiteur vient d'interagir avec la PAGE HOTE.
   *
   * POURQUOI CE SIGNAL TRAVERSE LA FRONTIERE PLUTOT QUE D'ETRE MESURE DANS
   * L'IFRAME. L'iframe ne recoit aucun evenement de souris ou de clavier de la
   * page qui l'entoure -- c'est la definition meme de son isolation. Quelqu'un
   * qui lit et fait defiler un article pendant dix minutes sans toucher au
   * panneau de tchat serait, vu de l'interieur du panneau, parfaitement
   * immobile : on l'annoncerait en veille alors qu'il est en train de lire.
   *
   * SANS PAYLOAD, ET STRICTEMENT LIMITE PAR LE LOADER. C'est un battement, pas
   * un flux d'evenements : le loader n'en envoie qu'un par periode (cf.
   * ACTIVITE_MIN_MS), et rien de ce que fait le visiteur -- ni la position du
   * pointeur, ni les touches frappees -- ne quitte la page hote. Le message dit
   * « il y a quelqu'un », il ne dit pas ce que cette personne fait.
   *
   * C'est l'iframe qui decide de la bascule en veille : elle seule connait
   * `SiteConfig.idleAfterSeconds`.
   */
  | HostMessage<'activity', undefined>
  | HostMessage<'visitor-hint', VisitorHint>
  /** Le visiteur a demande l'oubli local : l'iframe purge son etat en memoire. */
  | HostMessage<'reset', undefined>;

export type FrameToHostMessage =
  | FrameMessage<'ready', { version: string }>
  /** Jeton emis par le serveur : a memoriser en first-party. */
  | FrameMessage<'visitor-token', { visitorToken: string }>
  | FrameMessage<'consent', LocalConsent>
  | FrameMessage<'unread', { count: number }>
  | FrameMessage<'session', { hasActiveConversation: boolean }>
  /** Fermeture demandee depuis l'interieur (croix, touche Echap). */
  | FrameMessage<'request-close', undefined>
  | FrameMessage<'availability', { online: boolean }>
  /**
   * Un agent vient de repondre, et ce message est nouveau pour le visiteur.
   *
   * L'iframe annonce le FAIT ; c'est le loader qui decide s'il faut sonner, car
   * lui seul connait l'etat du panneau dans la page hote et la visibilite de
   * l'onglet. Volontairement sans contenu : une notification n'a pas besoin du
   * texte du message, et le faire traverser la frontiere postMessage
   * l'exposerait sans necessite.
   */
  | FrameMessage<'agent-message', undefined>;

/**
 * Couple (type, payload) valide, sans les champs d'enveloppe.
 *
 * Ecrit en type mappe pour rester DISTRIBUTIF : un simple
 * `Omit<Union, 'source'>` fusionnerait les membres et laisserait passer un type
 * associe au payload d'un autre message.
 */
type MessageBody<TMessage extends { type: string; payload: unknown }> = {
  [K in TMessage['type']]: {
    type: K;
    payload: Extract<TMessage, { type: K }>['payload'];
  };
}[TMessage['type']];

export type HostMessageBody = MessageBody<HostToFrameMessage>;
export type FrameMessageBody = MessageBody<FrameToHostMessage>;

export function hostEnvelope(channel: string, body: HostMessageBody): HostToFrameMessage {
  return { source: HOST_SOURCE, v: PROTOCOL_VERSION, channel, ...body };
}

export function frameEnvelope(channel: string, body: FrameMessageBody): FrameToHostMessage {
  return { source: FRAME_SOURCE, v: PROTOCOL_VERSION, channel, ...body };
}

/**
 * Verification structurelle d'une enveloppe recue.
 *
 * Volontairement independante de zod : elle sert aussi au loader, dont le bundle
 * est charge sur chaque page du site hote et doit rester minuscule. Le contenu
 * du champ `payload`, lui, est valide par zod cote iframe (frame/schemas.ts).
 */
export function isEnvelope(
  value: unknown,
  expectedSource: string,
  expectedChannel: string,
): value is { source: string; v: number; channel: string; type: string; payload: unknown } {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    record.source === expectedSource &&
    record.v === PROTOCOL_VERSION &&
    record.channel === expectedChannel &&
    typeof record.type === 'string'
  );
}

/** Noms des parametres passes a l'iframe dans le FRAGMENT de son URL. */
export const FRAGMENT_KEYS = {
  /** Jeton visiteur. */
  token: 't',
  /** Nonce de canal. */
  channel: 'c',
  /** Origine de la page hote. */
  hostOrigin: 'o',
} as const;
